/**
 * DAMM v2 buy / sell helpers for the trade panel.
 *
 * All helpers return plain values (BigInt-as-strings) so React state can
 * hold them without BigInt serialization headaches.
 *
 * Single source of truth for the pool's live state — fetched via the CpAmm
 * SDK, cached per-pool-address so quotes and swaps don't double-fetch.
 */

/* global BigInt */
import {
  PublicKey,
  LAMPORTS_PER_SOL,
} from "@solana/web3.js";
import {
  NATIVE_MINT,
  TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
  getAccount,
  createAssociatedTokenAccountIdempotentInstruction,
} from "@solana/spl-token";
import BN from "bn.js";
import { sendTx } from "./sendTx";
import { SLURP_TREASURY } from "./solanaLaunch";

const TOKEN_DECIMALS_DEFAULT = 6;

// Meteora DAMM v2 host-fee kickback: 20% of the protocol fee (= ~4% of
// total swap fees) is routed to the wallet whose WSOL ATA is passed as
// `referralTokenAccount` on the swap. We were passing `null` here and
// leaving that money on Meteora's side. Route it to the slurp treasury.
// Pre-derived once at module load (treasury pubkey never changes).
const SLURP_TREASURY_PK = new PublicKey(SLURP_TREASURY);
const SLURP_HOST_FEE_ATA = getAssociatedTokenAddressSync(
  NATIVE_MINT,
  SLURP_TREASURY_PK,
  true, // allowOwnerOffCurve — treasury could be a multisig PDA
);
// Idempotent create ix prepended to every swap. Cheap (~1500 CU) when
// the ATA already exists; creates + pays rent (~0.002 SOL, one-time) the
// very first time. Self-healing if the ATA is ever closed.
function buildHostFeeCreateIx(payer) {
  return createAssociatedTokenAccountIdempotentInstruction(
    payer,
    SLURP_HOST_FEE_ATA,
    SLURP_TREASURY_PK,
    NATIVE_MINT,
  );
}

const loadCpAmm = async () => {
  const mod = await import("@meteora-ag/cp-amm-sdk");
  return mod;
};

// Cache one CpAmm client per connection. Fresh pool state is re-fetched on
// each call that needs it.
let _cpAmm = null;
async function getCpAmm(connection) {
  if (_cpAmm && _cpAmm._conn === connection) return _cpAmm;
  const { CpAmm } = await loadCpAmm();
  const c = new CpAmm(connection);
  c._conn = connection;
  _cpAmm = c;
  return c;
}

/** Preview a buy — tokens returned for `solAmount` SOL. */
export async function quoteBuy({ connection, poolAddress, solAmount, slippageBps = 100 }) {
  const cpAmm = await getCpAmm(connection);
  const poolState = await cpAmm.fetchPoolState(new PublicKey(poolAddress));
  const amountIn = new BN(Math.floor(Number(solAmount) * LAMPORTS_PER_SOL));
  const slippage = slippageBps / 100; // cp-amm-sdk expects a %, not bps
  const q = cpAmm.getQuote2({
    swapMode: 0,
    amountIn,
    inputTokenMint: NATIVE_MINT,
    slippage,
    currentPoint: new BN(Math.floor(Date.now() / 1000)),
    poolState,
    tokenADecimal: TOKEN_DECIMALS_DEFAULT,
    tokenBDecimal: 9,
    hasReferral: false,
  });
  return {
    inAmountRaw: amountIn.toString(),
    outAmountRaw: q.outputAmount.toString(),
    minOutRaw: (q.minimumAmountOut ?? q.outputAmount).toString(),
    priceImpactPct: Number(q.priceImpact?.toString?.() ?? 0),
    feeRaw: q.totalFee?.toString?.() ?? "0",
  };
}

/** Preview a sell — SOL returned for `tokenAmountRaw` raw tokens. */
export async function quoteSell({ connection, poolAddress, tokenAmountRaw, slippageBps = 100 }) {
  const cpAmm = await getCpAmm(connection);
  const poolState = await cpAmm.fetchPoolState(new PublicKey(poolAddress));
  const tokenMint = poolState.tokenAMint.equals(NATIVE_MINT)
    ? poolState.tokenBMint
    : poolState.tokenAMint;
  const amountIn = new BN(String(tokenAmountRaw));
  const slippage = slippageBps / 100;
  const q = cpAmm.getQuote2({
    swapMode: 0,
    amountIn,
    inputTokenMint: tokenMint,
    slippage,
    currentPoint: new BN(Math.floor(Date.now() / 1000)),
    poolState,
    tokenADecimal: TOKEN_DECIMALS_DEFAULT,
    tokenBDecimal: 9,
    hasReferral: false,
  });
  return {
    inAmountRaw: amountIn.toString(),
    outAmountRaw: q.outputAmount.toString(),
    minOutRaw: (q.minimumAmountOut ?? q.outputAmount).toString(),
    priceImpactPct: Number(q.priceImpact?.toString?.() ?? 0),
    feeRaw: q.totalFee?.toString?.() ?? "0",
  };
}

/**
 * Execute a buy. DAMM v2's cp-amm-sdk handles wrap/unwrap + ATA creation
 * internally — we just call swap() and the SDK's preInstructions wrap the
 * SOL, the swap runs, postInstructions unwrap any residual WSOL. One tx,
 * one signature.
 */
export async function buyToken({
  connection,
  wallet,
  poolAddress,
  tokenMint,
  solAmount,
  slippageBps = 100,
  onStage = () => {},
}) {
  if (!wallet?.publicKey) throw new Error("Wallet not connected");
  const owner = wallet.publicKey;
  const mint = new PublicKey(tokenMint);
  const poolPk = new PublicKey(poolAddress);

  const cpAmm = await getCpAmm(connection);
  const poolState = await cpAmm.fetchPoolState(poolPk);
  const amountIn = new BN(Math.floor(Number(solAmount) * LAMPORTS_PER_SOL));

  onStage("swapping");
  const slippage = slippageBps / 100;
  const quote = cpAmm.getQuote2({
    swapMode: 0,
    amountIn,
    inputTokenMint: NATIVE_MINT,
    slippage,
    currentPoint: new BN(Math.floor(Date.now() / 1000)),
    poolState,
    tokenADecimal: TOKEN_DECIMALS_DEFAULT,
    tokenBDecimal: 9,
    hasReferral: false,
  });

  // Pass poolState explicitly so cpAmm.swap() doesn't re-fetch — we just
  // fetched it a few lines up. Saves one getAccountInfo per buy.
  const swapTx = await cpAmm.swap({
    payer: owner,
    pool: poolPk,
    inputTokenMint: NATIVE_MINT,
    outputTokenMint: mint,
    amountIn,
    minimumAmountOut: quote.minimumAmountOut ?? quote.outputAmount,
    tokenAMint: poolState.tokenAMint,
    tokenBMint: poolState.tokenBMint,
    tokenAVault: poolState.tokenAVault,
    tokenBVault: poolState.tokenBVault,
    tokenAProgram: TOKEN_PROGRAM_ID,
    tokenBProgram: TOKEN_PROGRAM_ID,
    referralTokenAccount: SLURP_HOST_FEE_ATA,
    poolState,
  });
  // Prepend the idempotent ATA create so slurp's host-fee WSOL account
  // exists before Meteora tries to credit it. No-op when it already exists.
  swapTx.instructions.unshift(buildHostFeeCreateIx(owner));
  const sig = await sendTx(connection, wallet, swapTx);

  onStage("done");
  return {
    signature: sig,
    inAmountRaw: amountIn.toString(),
    outAmountRaw: quote.outputAmount.toString(),
    minOutRaw: (quote.minimumAmountOut ?? quote.outputAmount).toString(),
  };
}

/**
 * Execute a sell. Same story as buy — the SDK handles ATA creation + WSOL
 * wrap/unwrap automatically. One tx, one signature.
 */
export async function sellToken({
  connection,
  wallet,
  poolAddress,
  tokenMint,
  tokenAmountRaw,
  slippageBps = 100,
  onStage = () => {},
}) {
  if (!wallet?.publicKey) throw new Error("Wallet not connected");
  const owner = wallet.publicKey;
  const mint = new PublicKey(tokenMint);
  const poolPk = new PublicKey(poolAddress);

  const cpAmm = await getCpAmm(connection);
  const poolState = await cpAmm.fetchPoolState(poolPk);
  const amountIn = new BN(String(tokenAmountRaw));

  onStage("swapping");
  const slippage = slippageBps / 100;
  const quote = cpAmm.getQuote2({
    swapMode: 0,
    amountIn,
    inputTokenMint: mint,
    slippage,
    currentPoint: new BN(Math.floor(Date.now() / 1000)),
    poolState,
    tokenADecimal: TOKEN_DECIMALS_DEFAULT,
    tokenBDecimal: 9,
    hasReferral: false,
  });

  // DAMM v2's cp-amm-sdk appends an unwrap-WSOL ix to the swap's
  // postInstructions automatically when either side is NATIVE_MINT. So the
  // sell tx already closes the WSOL ATA and refunds native SOL. We do NOT
  // need a follow-up close — doing so throws InvalidAccountData.
  // Pass poolState so the SDK doesn't re-fetch (saves one getAccountInfo).
  const swapTx = await cpAmm.swap({
    payer: owner,
    pool: poolPk,
    inputTokenMint: mint,
    outputTokenMint: NATIVE_MINT,
    amountIn,
    minimumAmountOut: quote.minimumAmountOut ?? quote.outputAmount,
    tokenAMint: poolState.tokenAMint,
    tokenBMint: poolState.tokenBMint,
    tokenAVault: poolState.tokenAVault,
    tokenBVault: poolState.tokenBVault,
    tokenAProgram: TOKEN_PROGRAM_ID,
    tokenBProgram: TOKEN_PROGRAM_ID,
    referralTokenAccount: SLURP_HOST_FEE_ATA,
    poolState,
  });
  swapTx.instructions.unshift(buildHostFeeCreateIx(owner));
  const sig = await sendTx(connection, wallet, swapTx);

  onStage("done");
  return {
    signature: sig,
    inAmountRaw: amountIn.toString(),
    outAmountRaw: quote.outputAmount.toString(),
    minOutRaw: (quote.minimumAmountOut ?? quote.outputAmount).toString(),
  };
}

/**
 * Read raw token balance in a wallet. Returns the SUM across every token
 * account the wallet owns for that mint — handles the rare case where a
 * holder has tokens in multiple accounts (not just the canonical ATA),
 * and means a single-call RPC blip on the ATA fast-path doesn't silently
 * report 0 for a balance the user actually holds. Returns "0" only when
 * the wallet legitimately has no accounts for this mint.
 */
export async function getTokenBalance(connection, ownerPk, mint) {
  const mintPk = new PublicKey(mint);
  try {
    const r = await connection.getParsedTokenAccountsByOwner(
      ownerPk, { mint: mintPk }, "confirmed"
    );
    let total = 0n;
    for (const a of r.value) {
      const amt = a?.account?.data?.parsed?.info?.tokenAmount?.amount ?? "0";
      try { total += BigInt(amt); } catch {}
    }
    return total.toString();
  } catch (err) {
    try {
      const ata = getAssociatedTokenAddressSync(mintPk, ownerPk);
      const acct = await getAccount(connection, ata);
      return acct.amount.toString();
    } catch (err2) {
      console.warn("[getTokenBalance] both paths failed", { mint, err: err?.message, err2: err2?.message });
      throw err;
    }
  }
}

/** UI amount → raw amount string (no BigInt serialisation issues in React). */
export function toRawAmount(uiAmount, decimals = TOKEN_DECIMALS_DEFAULT) {
  const [whole, frac = ""] = String(uiAmount).split(".");
  const fracPadded = (frac + "0".repeat(decimals)).slice(0, decimals);
  const raw = `${whole}${fracPadded}`.replace(/^0+(?=\d)/, "");
  return raw || "0";
}

/** Raw amount → human-readable UI number. */
export function fromRawAmount(raw, decimals = TOKEN_DECIMALS_DEFAULT) {
  const s = String(raw ?? "0");
  if (s === "0") return 0;
  const d = decimals | 0;
  const whole = s.length > d ? s.slice(0, s.length - d) : "0";
  const frac = s.length > d ? s.slice(s.length - d) : s.padStart(d, "0");
  return Number(`${whole}.${frac}`);
}
